/* ArtJo winkelwagen: de enige plek waar iets aan de wagen wordt toegevoegd.
 *
 * Waarom dit bestaat
 * ------------------
 * Er stonden drie losse fetch-blokken in de winkel (aj-pdp, aj-bestsellers,
 * aj-wizard), elk met eigen foutafhandeling en eigen manier om de lade te
 * openen. Dat betekent drie keer dezelfde bug repareren. Vanaf nu roept elke
 * sectie AJ.voegToe() aan.
 *
 * Twee publieke functies:
 *   AJ.voegToe(items, knop)   items = [{id: 123, quantity: 1}, ...]
 *   AJ.haalCart()             geeft een Promise met het cart-object
 *
 * Wat dit NIET doet: een eigen winkelwagenlade bouwen. Dawn 16 heeft er al een
 * en die herrendert zichzelf via de Section Rendering API. Wij vragen die
 * secties op en geven ze door.
 *
 * Regels die hier hard in zitten:
 *  - Nooit twee calls tegelijk per knop. De knop gaat op disabled met
 *    aria-busy, en na afloop 1,2 seconde "Toegevoegd".
 *  - Elke call heeft een AbortController met 10 seconden timeout. Zonder dat
 *    blijft een knop op een trage verbinding eeuwig op "Bezig" staan.
 *  - Een 422 van Shopify (te weinig voorraad) wordt getoond in het Nederlands,
 *    op de knop zelf, niet in een alert.
 *  - Staat de lade uit in de themainstellingen, dan een toast rechtsonder plus
 *    een bijgewerkte cart-bubble. Geen harde doorverwijzing naar /cart, want
 *    dan verliest de klant zijn plek op de pagina.
 */
(function () {
  'use strict';

  var TIMEOUT_MS = 10000;
  var KLAAR_MS = 1200;
  var SECTIES = 'cart-drawer,cart-icon-bubble';

  function metTimeout(pad, opties) {
    var ctrl = new AbortController();
    var klok = setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS);
    opties = opties || {};
    opties.signal = ctrl.signal;
    return fetch(pad, opties)
      .then(function (r) { clearTimeout(klok); return r; })
      .catch(function (e) {
        clearTimeout(klok);
        if (e.name === 'AbortError') {
          throw new Error('De verbinding duurde te lang. Probeer het nog een keer.');
        }
        throw new Error('Er ging iets mis. Probeer het nog een keer.');
      });
  }

  /* Shopify geeft bij een 422 een Engelse melding terug die we niet aan de
     klant willen laten zien. Wat we wel weten is welke gevallen voorkomen. */
  function nederlandseFout(lichaam) {
    var m = (lichaam && (lichaam.description || lichaam.message)) || '';
    /* De winkel staat op Nederlands, dus Shopify antwoordt Nederlands. Alleen
       op Engels matchen betekende dat elke voorraadfout in de algemene regel
       eindigde. Gemeten 13 september 2026 tijdens review R1: 99 stuks van een
       variant met er 3 gaf "Lukte niet", terwijl de klant gewoon moest horen
       dat er nog maar drie waren. Beide talen staan er nu in. */
    if (/uitverkocht|niet meer op voorraad|sold out|out of stock|not available|unavailable/i.test(m)) {
      return 'Dit artikel is net uitverkocht.';
    }
    if (/niet genoeg|maar .* beschikbaar|slechts|kunnen er maar|voorraad|only .* left|can add only|items? available/i.test(m)) {
      var n = m.match(/(\d+)/);
      if (n) return 'Er zijn er nog ' + n[1] + ' op voorraad, die staan nu in je winkelwagen.';
      return 'Er zijn er niet genoeg meer op voorraad.';
    }
    if (/bestaat niet|niet gevonden|cannot find variant|does not exist/i.test(m)) {
      return 'Dit artikel bestaat niet meer.';
    }
    return 'Toevoegen lukte niet. Probeer het nog een keer.';
  }

  /* De winkelwagen opnieuw ophalen en de lade en het bolletje bijwerken.
     Nodig na een 422: Shopify legt dan vaak WEL een deel in de wagen (vraag je
     er 99 en zijn er 3, dan liggen er daarna 3 in) en antwoordt toch met een
     fout. Zonder deze stap bleef het winkelwagenbolletje op nul staan, bleef de
     lade dicht, en zag de klant alleen "Lukte niet" terwijl zijn wagen gevuld
     was. Gemeten en gerepareerd 13 september 2026 na review R1. */
  function herstelWagen() {
    return fetch('/?sections=' + SECTIES, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (secties) { if (secties) ververs(secties); })
      .catch(function () { /* niets: dit is een herstelpoging, geen belofte */ });
  }

  function labelVan(knop) {
    return knop.querySelector('[data-aj-knoplabel]') || knop;
  }

  function knopBezig(knop) {
    if (!knop) return null;
    var label = labelVan(knop);
    /* innerHTML, niet textContent. Het label van de snelknop op een
       productkaart is een plus-ICOON, dus een <svg>. Met textContent lazen we
       een lege string terug, schreven we die na afloop terug, en was het icoon
       voorgoed weg. Op /collections/all waren dat 33 knoppen tegelijk. */
    var oud = label.innerHTML;
    /* Een lopende hersteltimer afbreken, anders zet een oude timer straks een
       verouderd label terug. */
    if (knop._ajTimer) { clearTimeout(knop._ajTimer); knop._ajTimer = null; }
    knop.disabled = true;
    knop.setAttribute('aria-busy', 'true');
    label.textContent = 'Bezig...';
    return { knop: knop, label: label, oud: oud };
  }

  function knopKlaar(staat, tekst, isFout) {
    if (!staat) return;
    staat.label.textContent = tekst;
    staat.knop.removeAttribute('aria-busy');
    if (isFout) staat.knop.setAttribute('data-aj-fout', 'true');
    /* De timer wordt op de knop bewaard, zodat wie de knop intussen op
       uitverkocht zet hem kan afbreken. Zonder dat kon dit gebeuren: klant
       voegt toe, klikt binnen 1,2 seconde een uitverkochte kleur aan, de knop
       gaat terecht op "Tijdelijk uitverkocht" en disabled, en dan vuurt de oude
       timer die hem weer op "In winkelwagen" en klikbaar zet. */
    staat.knop._ajTimer = setTimeout(function () {
      staat.knop._ajTimer = null;
      /* Terugzetten wat er stond, icoon en al. */
      staat.label.innerHTML = staat.oud;
      staat.knop.disabled = false;
      staat.knop.removeAttribute('data-aj-fout');
    }, isFout ? KLAAR_MS * 2 : KLAAR_MS);
  }

  function toonToast(tekst) {
    var bestaand = document.querySelector('.aj-toast');
    if (bestaand) bestaand.remove();
    var el = document.createElement('div');
    el.className = 'aj-toast';
    el.setAttribute('role', 'status');
    el.textContent = tekst;
    document.body.appendChild(el);
    requestAnimationFrame(function () { el.setAttribute('data-aan', 'true'); });
    setTimeout(function () {
      el.removeAttribute('data-aan');
      setTimeout(function () { el.remove(); }, 260);
    }, 3200);
  }

  function ververs(secties) {
    /* De cart-bubble staat altijd in de header, ook als de lade uit staat. */
    if (secties && secties['cart-icon-bubble']) {
      var bubbel = document.getElementById('cart-icon-bubble');
      if (bubbel) {
        var nieuw = new DOMParser()
          .parseFromString(secties['cart-icon-bubble'], 'text/html')
          .querySelector('#cart-icon-bubble');
        if (nieuw) bubbel.innerHTML = nieuw.innerHTML;
      }
    }

    var lade = document.querySelector('cart-drawer');
    if (lade && typeof lade.renderContents === 'function' && secties) {
      lade.renderContents({ sections: secties });
      if (typeof lade.open === 'function') lade.open();
      return true;
    }
    return false;
  }

  var AJ = window.AJ || {};

  AJ.haalCart = function () {
    return metTimeout('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  };

  /* items: [{id: <variant id>, quantity: 1}, ...]
     knop:  het element dat geklikt is, mag ook null zijn. */
  AJ.voegToe = function (items, knop) {
    if (!items || !items.length) return Promise.resolve(null);
    if (knop && knop.disabled) return Promise.resolve(null);

    var staat = knopBezig(knop);

    return metTimeout('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, sections: SECTIES })
    })
      .then(function (r) {
        return r.json().then(function (lichaam) {
          if (!r.ok) throw new Error(nederlandseFout(lichaam));
          return lichaam;
        });
      })
      .then(function (lichaam) {
        var geopend = ververs(lichaam.sections);
        if (!geopend) {
          toonToast(items.length > 1
            ? items.length + ' artikelen toegevoegd'
            : 'Toegevoegd aan je winkelwagen');
        }
        knopKlaar(staat, 'Toegevoegd', false);
        document.dispatchEvent(new CustomEvent('aj:cart-gewijzigd', { detail: lichaam }));
        return lichaam;
      })
      .catch(function (e) {
        /* Eerst de wagen bijwerken, dan pas de melding: zie herstelWagen(). */
        herstelWagen();
        knopKlaar(staat, e.message.length > 42 ? 'Lukte niet' : e.message, true);
        if (e.message.length > 42) toonToast(e.message);
        document.dispatchEvent(new CustomEvent('aj:cart-gewijzigd'));
        return null;
      });
  };

  window.AJ = AJ;

  /* Eén luisteraar voor de hele winkel. Elke knop met data-aj-toevoegen werkt,
     ook knoppen die later in de DOM komen (winkelwagenlade, upsell). */
  document.addEventListener('click', function (e) {
    var knop = e.target.closest('[data-aj-toevoegen]');
    if (!knop) return;
    e.preventDefault();

    var ruw = knop.getAttribute('data-aj-toevoegen');
    var items;
    try {
      items = JSON.parse(ruw);
    } catch (err) {
      /* Kortste vorm: alleen een variant-id op de knop. */
      items = [{ id: Number(ruw), quantity: 1 }];
    }
    if (!Array.isArray(items)) items = [items];
    /* Een kaal variant-id op de knop (data-aj-toevoegen="12345") is geldige
       JSON, dus JSON.parse geeft het getal 12345 terug en de terugval hierboven
       werd nooit bereikt. Shopify kreeg dan [12345] in plaats van
       [{id: 12345, quantity: 1}] en deed niets. Gevonden op 13 september 2026
       op de plusknop van de winkelwagen-upsell. */
    items = items.map(function (x) {
      return typeof x === 'number' ? { id: x, quantity: 1 } : x;
    });

    AJ.voegToe(items, knop);
  });
})();
